import { cloneElement, useState, useEffect } from 'react'
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useHover,
    useFocus,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
} from '@floating-ui/react'
import styles from './ScoopTooltip.module.scss'

/**
 * ScoopTooltip - Tooltip component using Floating UI
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Element that triggers the tooltip
 * @param {string|React.ReactNode} props.title - Tooltip content
 * @param {string} [props.placement='top'] - Tooltip placement (top, bottom, left, right, etc.)
 * @param {boolean} [props.open] - Controlled open state
 * @param {number} [props.offset=8] - Distance in pixels between tooltip and trigger
 * @param {string} [props.className] - Additional CSS classes for tooltip
 * @param {number} [props.enterDelay=0] - Delay in ms before showing tooltip
 * @param {number} [props.leaveDelay=0] - Delay in ms before hiding tooltip
 */
export const ScoopTooltip = ({
    children,
    title,
    placement = 'top',
    open: controlledOpen,
    offset: offsetValue = 8,
    className = '',
    enterDelay = 0,
    leaveDelay = 0,
}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const [isReady, setIsReady] = useState(false)

    // Use controlled or uncontrolled state
    const isControlled = controlledOpen !== undefined
    const open = isControlled ? controlledOpen : uncontrolledOpen

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setUncontrolledOpen,
        placement,
        middleware: [offset(offsetValue), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    })

    // Prevent visibility flash - wait for positioning before showing
    useEffect(() => {
        if (open && floatingStyles.position) {
            const timer = requestAnimationFrame(() => {
                setIsReady(true)
            })
            return () => cancelAnimationFrame(timer)
        } else {
            setIsReady(false)
        }
    }, [open, floatingStyles.position])

    const hover = useHover(context, {
        move: false,
        delay: { open: enterDelay, close: leaveDelay },
    })
    const focus = useFocus(context)
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'tooltip' })

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

    // Don't render tooltip if no title or closed
    if (!title || !open) {
        return cloneElement(children, getReferenceProps({ ref: refs.setReference }))
    }

    const tooltipClassName = [styles.tooltip, className].filter(Boolean).join(' ')

    return (
        <>
            {cloneElement(children, getReferenceProps({ ref: refs.setReference }))}
            <FloatingPortal>
                <div
                    ref={refs.setFloating}
                    className={tooltipClassName}
                    style={{
                        ...floatingStyles,
                        opacity: isReady ? 1 : 0,
                        transition: isReady ? 'opacity 0.15s ease-out' : 'none',
                    }}
                    {...getFloatingProps()}
                >
                    {title}
                </div>
            </FloatingPortal>
        </>
    )
}

ScoopTooltip.displayName = 'ScoopTooltip'
